import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil, Plus, Search } from 'lucide-react';
import Modal from '../../../components/Modal';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';

const EMPTY_FORM = {
  client_name: '', client_code: '', contact_person: '', mobile: '',
  email: '', industry: '', gst_number: '', pan_number: '',
  number_of_employees: '', start_date: '', address: '', status: 'active',
  visible_to_user_ids: [],
};

const INDUSTRY_OPTIONS = [
  'Information Technology', 'Manufacturing', 'Retail', 'Healthcare',
  'Finance', 'Education', 'Logistics', 'Hospitality', 'Real Estate', 'Other',
];

function StatusDot({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`status-pill ${isActive ? 'status-done' : 'status-cancelled'}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: isActive ? 'var(--status-success-fg)' : 'var(--text-muted)',
      }} />
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function MemberPicker({ members, value, onChange }) {
  const [search, setSearch] = React.useState('');
  function toggle(userId) {
    const id = Number(userId);
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  }
  const filtered = search.trim()
    ? members.filter(m => m.name?.toLowerCase().includes(search.toLowerCase()))
    : members;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-muted)' }}>
      <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)' }}>
        <input
          type="text"
          placeholder="Search members…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }}
        />
      </div>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
      {filtered.length === 0 && (
        <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No members found</div>
      )}
      {filtered.map(m => {
        const checked = value.includes(Number(m.user_id));
        const label   = m.name;
        return (
          <label
            key={m.user_id}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 14px', cursor: 'pointer',
              borderBottom: '1px solid var(--border)',
              background: checked ? 'var(--bg-card)' : 'transparent',
              transition: 'background .15s',
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(m.user_id)}
              style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{label}</span>
          </label>
        );
      })}
      </div>
    </div>
  );
}

export default function Clients() {
  const { appId } = useParams();
  const { myRole, searchQuery } = useWorkspace();
  const api = useApi();

  const isAdmin = true; // all members can add/edit clients

  const [clients, setClients]     = useState([]);
  const [members, setMembers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [saveErr, setSaveErr]     = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [deleteErr, setDeleteErr] = useState(null);

  // The page's own search box wins when the user types into it; otherwise
  // fall back to whatever's in the top navbar search so that also filters this list.
  const effectiveSearch = search || searchQuery || '';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (effectiveSearch) params.search = effectiveSearch;
      if (statusFilter) params.status = statusFilter;
      setClients(await api.listContractClients(appId, params));
    } catch (e) {
      setError(e.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [appId, effectiveSearch, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.listAppMembers(appId).then(data => {
      setMembers(((data && data.items) || []).map(m => ({
        user_id: m.user_id,
        name: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || `User ${m.user_id}`,
        role: m.role,
      })));
    }).catch(() => {});
  }, [appId]);

  function openCreate() {
    setEditing(null); setForm(EMPTY_FORM); setSaveErr(null); setModalOpen(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({
      client_name: c.client_name || '', client_code: c.client_code || '',
      contact_person: c.contact_person || '', mobile: c.mobile || '',
      email: c.email || '', industry: c.industry || '',
      gst_number: c.gst_number || '', pan_number: c.pan_number || '',
      number_of_employees: c.number_of_employees ?? '',
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      address: c.address || '', status: c.status || 'active',
      visible_to_user_ids: c.visible_to_user_ids || [],
    });
    setSaveErr(null); setModalOpen(true);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeleteErr(null);
    try {
      await api.deleteContractClient(appId, deleting.id);
      setDeleting(null);
      load();
    } catch (e) {
      setDeleteErr(e.message || 'Delete failed');
    }
  }

  async function handleSave() {
    if (!form.client_name.trim()) { setSaveErr('Client name is required'); return; }
    if (!form.client_code.trim()) { setSaveErr('Client code is required'); return; }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setSaveErr('Please enter a valid email address (e.g. name@example.com)'); return;
    }
    if (form.pan_number.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan_number.trim().toUpperCase())) {
      setSaveErr('Please enter a valid PAN number (e.g. ABCDE1234F)'); return;
    }
    setSaving(true); setSaveErr(null);
    try {
      const body = {
        ...form,
        number_of_employees: form.number_of_employees === '' ? null : Number(form.number_of_employees),
        start_date: form.start_date === '' ? null : form.start_date,
      };
      if (editing) await api.updateContractClient(appId, editing.id, body);
      else         await api.createContractClient(appId, body);
      setModalOpen(false);
      load();
    } catch (e) {
      setSaveErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const totalClients  = clients.length;
  const activeClients = clients.filter(c => c.status === 'active').length;

  const modalFooter = (
    <div className="form-actions">
      <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
      <button className="btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Client'}
      </button>
    </div>
  );

  return (
    <div className="tl-page">
      <h1 className="page-title">Clients</h1>
      {/* Stats strip */}
      <div className="home-stats-strip" style={{ gridTemplateColumns: 'repeat(3, minmax(100px, 160px))', marginBottom: 20 }}>
        {[
          { label: 'Total Clients', value: totalClients },
          { label: 'Active',        value: activeClients },
          { label: 'Inactive',      value: totalClients - activeClients },
        ].map(s => (
          <div key={s.label} className="stat-tile">
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="tl-toolbar">
        <div className="tl-search-wrap">
          <Search className="tl-search-icon" size={14} />
          <input
            className="tl-search-input"
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="tl-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>
        <select className="tl-select" value={statusFilter} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {isAdmin && (
          <button className="btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Add Client
          </button>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <div className="tl-loading"><span className="tl-spinner" /> Loading…</div>
      ) : clients.length === 0 ? (
        <div className="tl-empty">
          <div className="tl-empty-title">No clients found</div>
          <p className="tl-empty-sub">Add your first client to get started.</p>
        </div>
      ) : (
        <div className="entity-table-wrap">
          <table className="entity-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Code</th>
                <th>Contact</th>
                <th>Industry</th>
                <th>Employees</th>
                <th>Status</th>
                <th>Visible To</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => {
                const visibleNames = (c.visible_to_user_ids || []).map(uid => {
                  const m = members.find(x => x.user_id === uid);
                  return m ? m.name : null;
                }).filter(Boolean);
                return (
                  <tr key={c.id}>
                    <td title={[c.client_name, c.email].filter(Boolean).join(' · ')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: 'var(--brand-light)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: 'var(--brand)', flexShrink: 0,
                        }}>
                          {initials(c.client_name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.4, whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.client_name}</div>
                          {c.email && <div className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 2, whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td title={c.client_code} style={{ fontFamily: 'monospace', fontSize: 13 }}>{c.client_code}</td>
                    <td title={[c.contact_person, c.mobile].filter(Boolean).join(' · ')}>
                      <div style={{ fontSize: 14, lineHeight: 1.4, whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.contact_person || <span className="muted">—</span>}</div>
                      {c.mobile && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{c.mobile}</div>}
                    </td>
                    <td title={c.industry}>{c.industry || <span className="muted">—</span>}</td>
                    <td>{c.number_of_employees ?? <span className="muted">—</span>}</td>
                    <td><StatusDot status={c.status} /></td>
                    <td title={visibleNames.length === 0 ? 'All' : visibleNames.join(', ')}>
                      {visibleNames.length === 0
                        ? <span className="muted" style={{ fontSize: 12 }}>All</span>
                        : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {visibleNames.map(n => (
                              <span key={n} style={{
                                fontSize: 11, fontWeight: 600, padding: '1px 6px',
                                borderRadius: 4, background: 'var(--brand-light)', color: 'var(--brand)',
                              }}>{n}</span>
                            ))}
                          </div>
                      }
                    </td>
                    <td className="row-actions">
                      <button className="btn-link-sm" onClick={() => setViewModal(c)}>View</button>
                      {isAdmin && (
                        <button className="btn-link-sm" onClick={() => openEdit(c)}>Edit</button>
                      )}
                      {isAdmin && (
                        <button className="btn-link-sm" style={{ color: 'var(--status-danger-fg)' }} onClick={() => { setDeleting(c); setDeleteErr(null); }}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editing ? 'Edit Client' : 'Add Client'}
        onClose={() => setModalOpen(false)}
        size="lg"
        footer={modalFooter}
      >
        <div className="org-edit-form">
          {/* Corporate Details */}
          <div className="org-form-section">
            <div className="org-form-section-title">Corporate Details</div>
            <div className="org-form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">
                  <span>Client Name <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
                  <input className="form-input" maxLength={100} value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
                </label>
              </div>
              <label className="form-label">
                <span>Status</span>
                <select className="form-input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
              <label className="form-label">
                <span>Client Code <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
                <input className="form-input" maxLength={20} value={form.client_code} onChange={e => setForm(f => ({ ...f, client_code: e.target.value }))} />
              </label>
              <label className="form-label">
                <span>GST Number</span>
                <input className="form-input" maxLength={15} style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} value={form.gst_number} onChange={e => setForm(f => ({ ...f, gst_number: e.target.value.replace(/[^a-zA-Z0-9]/g, '') }))} />
              </label>
              <label className="form-label">
                <span>PAN Number</span>
                <input className="form-input" maxLength={10} style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} value={form.pan_number} onChange={e => setForm(f => ({ ...f, pan_number: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() }))} />
              </label>
              <div style={{ gridColumn: 'span 3' }}>
                <label className="form-label">
                  <span>Industry</span>
                  <select className="form-input" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}>
                    <option value="">— select —</option>
                    {INDUSTRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div className="org-form-section">
            <div className="org-form-section-title">Visibility</div>
            <div className="org-form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="form-label">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span>Visible To Members</span>
                  {form.visible_to_user_ids.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                      {form.visible_to_user_ids.length} selected
                    </span>
                  )}
                </div>
                <MemberPicker
                  members={members}
                  value={form.visible_to_user_ids}
                  onChange={ids => setForm(f => ({ ...f, visible_to_user_ids: ids }))}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                  Leave empty to make visible to all members. Admins always see all clients.
                </span>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="org-form-section">
            <div className="org-form-section-title">Contact Details</div>
            <div className="org-form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <label className="form-label">
                <span>Contact Person</span>
                <input className="form-input" maxLength={100} value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} />
              </label>
              <label className="form-label">
                <span>Email</span>
                <input type="email" className="form-input" maxLength={254} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </label>
              <label className="form-label">
                <span>Mobile</span>
                <input className="form-input" value={form.mobile} maxLength={10} onChange={e => setForm(f => ({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
              </label>
              <div style={{ gridColumn: 'span 3' }}>
                <label className="form-label">
                  <span>Address</span>
                  <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} maxLength={1000} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  {form.address.length > 900 && (
                    <span style={{ fontSize: 11, color: form.address.length >= 1000 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 2 }}>
                      {form.address.length}/1000
                    </span>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="org-form-section">
            <div className="org-form-section-title">Additional Info</div>
            <div className="org-form-grid">
              <label className="form-label">
                <span>No. of Employees</span>
                <input type="text" inputMode="numeric" className="form-input" maxLength={4} value={form.number_of_employees} onChange={e => setForm(f => ({ ...f, number_of_employees: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) }))} />
              </label>
              <label className="form-label">
                <span>Onboarding Date</span>
                <input type="date" className="form-input" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </label>
            </div>
          </div>
        </div>

        {saveErr && <div className="banner banner-error" style={{ marginTop: 8 }}>{saveErr}</div>}
      </Modal>

      {/* View Modal */}
      {viewModal && (
        <Modal
          open
          title="Client Details"
          onClose={() => setViewModal(null)}
          size="lg"
          footer={
            <div className="form-actions">
              {isAdmin && (
                <button className="btn-secondary" onClick={() => { setViewModal(null); openEdit(viewModal); }}>
                  <Pencil size={13} style={{ marginRight: 4 }} />Edit
                </button>
              )}
              <button className="btn-primary" onClick={() => setViewModal(null)}>Close</button>
            </div>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 20px' }}>
            {[
              ['Client Name', viewModal.client_name],
              ['Client Code', viewModal.client_code],
              ['Status', viewModal.status],
              ['Contact Person', viewModal.contact_person],
              ['Email', viewModal.email],
              ['Mobile', viewModal.mobile],
              ['GST Number', viewModal.gst_number],
              ['PAN Number', viewModal.pan_number],
              ['Industry', viewModal.industry],
              ['Employees', viewModal.number_of_employees],
              ['Onboarding Date', viewModal.start_date ? viewModal.start_date.slice(0, 10) : null],
            ].map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>{v || <span className="muted">—</span>}</div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 4 }}>Visible To</div>
              <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>
                {(() => {
                  const ids = viewModal.visible_to_user_ids || [];
                  if (ids.length === 0) return 'All members';
                  const names = ids.map(uid => {
                    const m = members.find(x => x.user_id === uid);
                    return m ? m.name : `User ${uid}`;
                  });
                  return names.join(', ');
                })()}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 4 }}>Address</div>
              <div style={{ fontSize: 14, color: 'var(--text-primary)', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{viewModal.address || <span className="muted">—</span>}</div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      {deleting && (
        <Modal
          open
          title="Delete Client"
          onClose={() => setDeleting(null)}
          size="sm"
          footer={
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
            Are you sure you want to delete <strong>{deleting.client_name}</strong>? This action cannot be undone.
          </p>
          {deleteErr && <div className="banner banner-error" style={{ marginTop: 12 }}>{deleteErr}</div>}
        </Modal>
      )}
    </div>
  );
}
